import {
  parseProposalToWebData,
  getSlugSetForWebTemplateStaticPages,
} from './proposalToWebData';
import { fetchProposal } from '../requests/webProposal';
import { fetchWebTemplate } from '../requests/webTemplates';
import { preserveStaticPagesOnRegen } from '../utils/regenStaticPages';
import {
  setVariableCatalogSource,
  loadVariableCatalog,
} from '../utils/variableCatalogRegistry';
import { variableValueMap } from '../utils/variableCatalog';
import { resolveVariableNodesDeep } from '../utils/variableNodes';
import { loadWebLinks, setWebLinkSource } from '../utils/webLinkRegistry';
import { indexWebLinks, resolveBankLinksDeep } from '../utils/webLinks';
import { refreshVariableTablesDeep } from '../utils/webTableRefresh';
import { fetchGlobalRules } from '../../requests/globalRules';
import {
  collectUsedProposalStyles,
  hasStyleMappingChoices,
  hasStyleMappingQuestions,
  identityStyleMapping,
  planThemeStyleMapping,
} from '../../shared/theme/styleMapping';
import { isThemeConfigured, withThemeConfiguration } from '../../shared/theme/themeConfiguration';
import { applyThemeStyleMapping } from './applyThemeStyleMapping';
import {
  getRuleValue,
  resolveHiddenProposalBoxTypes,
  WEB_CHAPTER_SEPARATION_TITLE_ONLY,
} from '../../adminRules/rulesRegistry';

/**
 * Construit le `web_data` d'un proposal : chargement des données, parsing avec
 * les variables résolues, et préservation des pages statiques en régénération.
 *
 * Point d'entrée unique des deux chemins de génération (bouton « Générer un
 * site web » et arrivée sur /web-editor avec ?regen=1 ou un site vide), pour
 * qu'ils ne divergent plus — notamment sur les variables, qui doivent être
 * transmises au parser dans tous les cas.
 *
 * Ne persiste rien et ne navigue nulle part : les appelants n'écrivent pas par
 * le même mécanisme (`saveWebData` d'un côté, `persistWebDraft` de l'autre).
 *
 * @param {object} params
 * @param {string} params.proposalId
 * @param {string|number|null} params.templateId - template à appliquer (déjà résolu)
 * @param {{ id: string|number, type: string }|null} [params.documentIdentity]
 *        identité du document source (`oid` → opportunité, `ofid` → offre) :
 *        c'est elle, et non l'identifiant de proposal, qui adresse la route des
 *        variables. Persistée dans `projectConfig` pour que l'éditeur autonome
 *        `/web-editor/:proposalId` la retrouve.
 * @param {object|null} [params.previousWebData] - web_data avant régénération (null = première génération)
 * @param {boolean} [params.templateExplicitlyChosen] - un template a été choisi explicitement (URL ?templateId=)
 * @param {(request: { plan, used, theme, documentConfiguration }) => Promise<object>} [params.chooseStyleMapping]
 *        demande à l'utilisateur quel style du site prend chaque style du
 *        document, quand le plan a des questions (cf. StyleMappingModal).
 *        Absent : les propositions du plan s'appliquent.
 * @param {boolean} [params.reviewStyleMapping] - demander même sans question
 *        (choix tous mémorisés) : l'utilisateur veut revoir la correspondance.
 * @returns {Promise<object>} le web_data généré
 * @throws {Error} `no_template` si aucun template n'est résolvable
 */
export function buildWebDataForProposal({ proposalId, ...options }) {
  return buildWebData({
    ...options,
    loadContent: () => fetchProposal(proposalId).then((proposal) => proposal.content),
  });
}

/**
 * Construit le `web_data` d'un site web seul : le modèle seul le sème —
 * squelette, pages, thème, compositions — sans proposal classique à parser.
 * Mêmes résolutions que la conversion d'un proposal (variables, images et
 * tableaux du CRM, banque de liens), sur le document qui porte le site.
 *
 * @param {object} params
 * @param {string|number} params.templateId - modèle « Site web seul »
 * @param {{ id: string|number, type: string }} params.documentIdentity
 *        opportunité ou offre qui porte le site
 * @returns {Promise<object>} le web_data généré
 */
export function buildWebDataForTemplate({ templateId, documentIdentity }) {
  return buildWebData({
    templateId,
    documentIdentity,
    loadContent: () => Promise.resolve({}),
    templatePagesOnly: true,
  });
}

/* Sans contenu, le parser pose quand même une page de document par défaut :
   un site web seul n'a que les pages de son modèle. */
function withTemplatePagesOnly(webData) {
  const pages = Object.fromEntries(
    Object.entries(webData.pages || {}).filter(([, page]) => page?.type === 'static'),
  );
  return {
    ...webData,
    pages,
    pageOrder: (webData.pageOrder || []).filter((slug) => pages[slug]),
  };
}

async function buildWebData({
  loadContent,
  templateId,
  documentIdentity = null,
  previousWebData = null,
  templateExplicitlyChosen = false,
  chooseStyleMapping = null,
  reviewStyleMapping = false,
  templatePagesOnly = false,
}) {
  setVariableCatalogSource(documentIdentity);
  setWebLinkSource(documentIdentity);

  const [content, template, catalog, globalRules] = await Promise.all([
    loadContent(),
    templateId ? fetchWebTemplate(templateId) : Promise.resolve(null),
    // Le registre trace l'échec au lieu de l'avaler : une génération sans
    // variables reste possible, mais elle ne passe plus inaperçue.
    loadVariableCatalog({ force: true }),
    // Mémoïsé, et replié sur `{}` en cas d'échec : une règle illisible retombe
    // sur les défauts du registre, elle ne bloque pas la génération.
    fetchGlobalRules(),
  ]);

  if (!template) throw new Error('no_template');

  const parse = templatePagesOnly ? (...args) => withTemplatePagesOnly(parseProposalToWebData(...args))
    : parseProposalToWebData;
  const parsed = parse(
    content,
    template,
    variableValueMap(catalog),
    {
      hiddenBoxTypes: resolveHiddenProposalBoxTypes(globalRules),
      chapterSeparationTitleOnly: !!getRuleValue(
        globalRules,
        WEB_CHAPTER_SEPARATION_TITLE_ONLY,
      ),
    },
  );

  // Styles du document → styles du thème du site. Le choix précédent est
  // mémorisé : seuls les styles nouveaux ou orphelins sont redemandés.
  // Template jamais configuré : le thème du site est la configuration du
  // document, chaque style y est le sien.
  const used = collectUsedProposalStyles(content);
  const configured = isThemeConfigured(template.theme);
  const siteTheme = configured
    ? template.theme
    : withThemeConfiguration(template.theme, content?.configuration);
  const plan = planThemeStyleMapping({
    used,
    theme: siteTheme,
    previousMapping: configured ? previousWebData?.projectConfig?.styleMapping : {},
  });
  let mapping = identityStyleMapping(used);
  if (configured) {
    const mustAsk = hasStyleMappingQuestions(plan)
      || (reviewStyleMapping && hasStyleMappingChoices(plan));
    mapping = chooseStyleMapping && mustAsk
      ? await chooseStyleMapping({
        plan, used, theme: siteTheme, documentConfiguration: content?.configuration,
      })
      : {};
  }
  const themed = applyThemeStyleMapping(parsed, {
    used, plan, mapping, theme: siteTheme, remember: configured,
  });

  const merged = previousWebData?.pages
    ? await mergePreviousStaticPages({
      data: themed, previousWebData, templateId, templateExplicitlyChosen,
    })
    : themed;

  // Parité template → document : le squelette et les pages statiques recopiés
  // depuis le template portent des variables non résolues ; on les remplit
  // depuis le proposal, en laissant leur nom à celles qui n'ont pas de valeur.
  const withVariables = resolveVariableNodesDeep(merged, catalog.byKey);

  /* Un template copie aussi des adresses de la banque et des tableaux branchés
     sur le document. La génération ne les relisait pas : un site naissait avec
     l'adresse qu'avait le lien le jour où le modèle a été fait, et un tableau
     vide jusqu'à ce que quelqu'un pense à « Rafraîchir les données ». Les deux
     échecs sont silencieux — un site se génère sans banque ni tables. */
  const bankLinks = await loadWebLinks({ force: true }).catch(() => []);
  const withLinks = bankLinks.length
    ? resolveBankLinksDeep(withVariables, indexWebLinks(bankLinks))
    : withVariables;
  const resolved = await refreshVariableTablesDeep(withLinks, documentIdentity)
    .catch(() => withLinks);

  return {
    ...resolved,
    projectConfig: { ...resolved.projectConfig, documentIdentity },
  };
}

async function mergePreviousStaticPages({
  data, previousWebData, templateId, templateExplicitlyChosen,
}) {
  const previousTemplateId = previousWebData.projectConfig?.webTemplateId;
  const switchedTemplate = !!templateExplicitlyChosen
    && previousTemplateId != null
    && String(previousTemplateId) !== String(templateId);

  let oldTemplateStaticSlugs = null;
  if (switchedTemplate) {
    const oldTmpl = await fetchWebTemplate(previousTemplateId).catch(() => null);
    oldTemplateStaticSlugs = oldTmpl
      ? getSlugSetForWebTemplateStaticPages(oldTmpl)
      : null;
  }

  return preserveStaticPagesOnRegen(data, previousWebData, {
    switchedTemplate,
    oldTemplateStaticSlugs,
  });
}
